/**
 * Anti-auto-pilot mirror (WAKEUP_MVP1.1 T4) — *« Force la compréhension vs la mémoire visuelle. »*
 *
 * Stratégie V1 : miroir horizontal (file swap a↔h, b↔g, c↔f, d↔e). Le tour de
 * jouer ne change pas ; les pièces conservent leur couleur ; seule la lecture
 * gauche/droite est inversée.
 *
 * Pourquoi : la mémoire visuelle de Léon associe une ouverture à un layout
 * spatial. Le mirror casse ce raccourci tout en gardant la sémantique stratégique
 * intacte — la déviation reste correcte, juste sur la file miroir.
 */

const FILE_MIRROR: Record<string, string> = {
  a: 'h',
  b: 'g',
  c: 'f',
  d: 'e',
  e: 'd',
  f: 'c',
  g: 'b',
  h: 'a',
};

const CASTLING_MIRROR: Record<string, string> = {
  K: 'Q',
  Q: 'K',
  k: 'q',
  q: 'k',
};

const CASTLING_ORDER = ['K', 'Q', 'k', 'q'] as const;

export interface MirroredPosition {
  fen: string;
  /** UCI miroir du coup attendu (e.g. e2e4 → d2d4). */
  expectedUci: string;
  /** Square `from` miroir. */
  fromMirror: string;
  /** Square `to` miroir. */
  toMirror: string;
}

/**
 * Applique un miroir horizontal à une FEN.
 *
 * @throws Error si la FEN est malformée (moins de 4 champs ou rang invalide).
 */
export function mirrorFen(fen: string): string {
  const parts = fen.trim().split(/\s+/);
  if (parts.length < 4) {
    throw new Error('FEN malformée : moins de 4 champs');
  }
  const [board, turn, castling, ep, half = '0', full = '1'] = parts;

  if (!board || !turn || !castling || !ep) {
    throw new Error('FEN malformée : champ vide');
  }

  const ranks = board.split('/');
  if (ranks.length !== 8) {
    throw new Error(`FEN malformée : ${ranks.length} rangs au lieu de 8`);
  }
  const mirroredRanks = ranks.map((rank) => rank.split('').reverse().join(''));
  const mirroredBoard = mirroredRanks.join('/');

  const mirroredCastling = mirrorCastling(castling);
  const mirroredEp = ep === '-' ? '-' : mirrorSquare(ep);

  return [mirroredBoard, turn, mirroredCastling, mirroredEp, half, full].join(' ');
}

function mirrorCastling(castling: string): string {
  if (castling === '-') return '-';
  // Le filter pré-`map` initial était mort (CASTLING_MIRROR[c] !== undefined sur
  // c déjà swappé est toujours vrai). On filtre le résultat du map à la place
  // pour drop tout caractère hors KQkq. Spec : docs/WAKEUP_MVP1.2.md §3 T0 #2 important.
  const swapped = castling
    .split('')
    .map((c) => CASTLING_MIRROR[c])
    .filter((c): c is string => c !== undefined);
  // Re-trier en ordre canonique (KQkq) pour rester compatible avec les parsers stricts.
  const set = new Set(swapped);
  let out = '';
  for (const c of CASTLING_ORDER) {
    if (set.has(c)) out += c;
  }
  return out === '' ? '-' : out;
}

/**
 * Renvoie l'UCI miroir d'un coup (e.g. "e2e4" → "d2d4", "e7e8q" → "d7d8q").
 */
export function mirrorUciMove(uci: string): string {
  if (uci.length < 4) {
    throw new Error(`UCI invalide : ${uci}`);
  }
  const fromFile = uci[0]!;
  const fromRank = uci[1]!;
  const toFile = uci[2]!;
  const toRank = uci[3]!;
  const promo = uci.length > 4 ? uci.slice(4) : '';
  if (!FILE_MIRROR[fromFile] || !FILE_MIRROR[toFile]) {
    throw new Error(`UCI files invalides : ${uci}`);
  }
  return `${FILE_MIRROR[fromFile]}${fromRank}${FILE_MIRROR[toFile]}${toRank}${promo}`;
}

/**
 * Mirror d'une case (e.g. "e4" → "d4").
 */
export function mirrorSquare(sq: string): string {
  if (sq.length < 2) return sq;
  const file = sq[0]!;
  const rest = sq.slice(1);
  const mirrored = FILE_MIRROR[file];
  return mirrored ? mirrored + rest : sq;
}

/**
 * Construit une `MirroredPosition` à partir d'une FEN + UCI attendu.
 *
 * @throws si la FEN ou l'UCI est invalide.
 */
export function buildMirroredPosition(fenBefore: string, expectedUci: string): MirroredPosition {
  const fen = mirrorFen(fenBefore);
  const mirroredUci = mirrorUciMove(expectedUci);
  const fromMirror = mirroredUci.slice(0, 2);
  const toMirror = mirroredUci.slice(2, 4);
  return {
    fen,
    expectedUci: mirroredUci,
    fromMirror,
    toMirror,
  };
}
